/**
 * Edit an existing client (US3 — FR-007 / SC-004). Search a client, apply additive edits (HQ country,
 * name, retire/mark-duplicate) via `upsertClient`, and show its audit trail. Editing the commercial
 * area requires sales/admin (server-enforced). Multi-country legal entities are separate
 * BillingIdentities (ADR-0001), managed on the Billing identities screen.
 */
import { useState } from 'react';
import type { SessionUser } from '../services/auth';
import type { Client } from '../types/entities';
import { upsertClient } from '../services/onboarding';
import { searchClients } from '../services/clients';
import { AuditTrail } from '../components/AuditTrail';

export function EditClient({ user }: { user: SessionUser }) {
  const canEdit = user.role === 'sales' || user.role === 'admin';

  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<Client[]>([]);
  const [client, setClient] = useState<Client | null>(null);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [isClient, setIsClient] = useState(true);
  const [duplicateOf, setDuplicateOf] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const find = async () => {
    setError(null);
    setNote(null);
    try {
      setMatches(await searchClients(query));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed');
    }
  };

  const edit = (c: Client) => {
    setClient(c);
    setName(c.canonical_name ?? '');
    setCountry(c.country ?? '');
    setIsClient(c.is_client ?? true);
    setDuplicateOf(c.duplicate_of ?? '');
    setNote(null);
    setError(null);
  };

  const save = async () => {
    if (!client) return;
    if (country && !/^[A-Z]{2}$/.test(country)) {
      setError('Country must be a 2-letter ISO code (e.g. US, MX).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await upsertClient({
        canonical_client_id: client.canonical_client_id,
        canonical_name: name,
        country,
        is_client: isClient,
        duplicate_of: duplicateOf.trim() || undefined,
      });
      setClient(res.doc);
      setNote(`Saved (event ${res.event_id}).`);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h1>Edit client</h1>
      {!canEdit && (
        <p style={banner}>Editing the commercial area requires the sales or admin role — read-only.</p>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder="Search client (e.g. Arguilea)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1 }}
        />
        <button type="button" onClick={() => void find()} disabled={!query}>
          Search
        </button>
      </div>
      {matches.length > 0 && (
        <ul>
          {matches.map((c) => (
            <li key={c.canonical_client_id}>
              <code>{c.canonical_client_id}</code> {c.canonical_name}{' '}
              {c.country ? `(${c.country})` : ''}{' '}
              <button type="button" onClick={() => edit(c)}>
                edit
              </button>
            </li>
          ))}
        </ul>
      )}

      {client && (
        <>
          <h2>{client.canonical_client_id}</h2>
          <fieldset disabled={!canEdit || busy} style={fs}>
            <legend>Client fields</legend>
            <label style={rowStyle}>
              <span style={{ width: 160 }}>Canonical name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label style={rowStyle}>
              <span style={{ width: 160 }}>Country (ISO-2 HQ)</span>
              <input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} />
            </label>
            <label style={rowStyle}>
              <span style={{ width: 160 }}>Is client</span>
              <input type="checkbox" checked={isClient} onChange={(e) => setIsClient(e.target.checked)} />
            </label>
            <label style={rowStyle}>
              <span style={{ width: 160 }}>Duplicate of</span>
              <input
                value={duplicateOf}
                onChange={(e) => setDuplicateOf(e.target.value.toUpperCase())}
                placeholder="e.g. CC-148 (mark as duplicate)"
              />
            </label>
            <button type="button" onClick={() => void save()} disabled={!name}>
              Save
            </button>
          </fieldset>
          {note && <p style={{ color: 'green' }}>{note}</p>}
          <AuditTrail collection="clients" documentId={client.canonical_client_id} refreshKey={refreshKey} />
        </>
      )}

      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}
    </section>
  );
}

const banner: React.CSSProperties = { background: '#fff7e6', border: '1px solid #ffd591', padding: 8, borderRadius: 4 };
const fs: React.CSSProperties = { border: '1px solid #ddd', padding: 12, marginTop: 12 };
const rowStyle: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0' };
